import type { Line, Paragraph, ParaAttrs, TabStop, TextAttrs } from '@proofroom/schema';
import { resolveParaAttrs, resolveRunAttrs, type StyleLookup } from '../model/attrs';
import { recordLine, recordPositioned } from './fidelity';

/** Tolerance (pt) beyond which we force the browser to match InDesign's line width. */
export const WIDTH_TOLERANCE = 0.5;
/** InDesign's default tab interval when a paragraph defines no stop past the cursor. */
export const DEFAULT_TAB = 36;
/** A line's own start and the frame's column rect that disagree by more than this (pt) sit in different columns. */
export const ORIGIN_TOLERANCE = 0.1;
/** InDesign's auto page number marker inside story text. */
export const AUTO_PAGE_NUMBER = '\u0018';
/**
 * InDesign's flush space (exported as an em quad). In a justified line it swallows all the slack and
 * the ordinary word spaces keep their natural width, which is how the footers put the page number
 * hard left and the running title hard right.
 */
export const FLUSH_SPACE = '\u2001';

export interface SpanProps {
  text: string;
  fontFamily: string;
  fontSize: number;
  fill: string;
  /** pt, derived from tracking (1/1000 em) */
  letterSpacing: number;
  /** absolute x when the span was positioned by a tab stop or justification */
  x?: number;
  dy?: number;
  fontVariant?: 'small-caps';
  underline?: boolean;
  strikethrough?: boolean;
}

export type Anchor = 'start' | 'middle' | 'end';

export interface LineProps {
  key: string;
  paraId: string;
  frameId: string;
  start: number;
  end: number;
  x: number;
  y: number;
  width: number;
  ascent: number;
  descent: number;
  anchor: Anchor;
  spans: SpanProps[];
  /** set only when the natural width differs from InDesign's by more than the tolerance */
  textLength?: number;
  naturalWidth?: number;
  /** the paragraph's bullet, on its first line only, at the column edge plus the first line indent */
  marker?: SpanProps & { x: number };
}

export interface LineOptions {
  /** left edge of the column's text area (after insets), origin for tab stops */
  columnX?: number;
  /** page number substituted for InDesign's auto page number character */
  pageNumber?: string;
  /** true when this is the last line of its paragraph (affects justification) */
  lastInParagraph?: boolean;
}

/** Natural width of a span in pt. Injected so the renderer stays pure. */
export type Measure = (text: string, fontFamily: string, fontSize: number, letterSpacing: number) => number;

const CONTROL = /[\u0000-\u0008\u000a-\u001f]/;
const TRAILING = /[ \t\u00a0]/;

export function trackingToSpacing(tracking: number, pointSize: number): number {
  return (tracking / 1000) * pointSize;
}

export function applyCapitalization(text: string, cap: TextAttrs['capitalization']): string {
  return cap === 'allCaps' || cap === 'capToSmallCap' ? text.toUpperCase() : text;
}

/** InDesign special characters: the auto page number becomes the page number, other C0 controls vanish. */
export function substituteControls(text: string, pageNumber = ''): string {
  let out = '';
  for (const ch of text) {
    if (ch === AUTO_PAGE_NUMBER) out += pageNumber;
    else if (!CONTROL.test(ch)) out += ch;
  }
  return out;
}

/** Text of a line without trailing spaces (they belong to the line but carry no width), plus the composer's hyphen. */
export function lineDisplayText(line: Line, paragraph: Paragraph, pageNumber = ''): string {
  const raw = substituteControls(paragraph.text.slice(line.start, line.end), pageNumber).replace(/[ \t ]+$/, '');
  return line.hyphenated ? raw + '-' : raw;
}

export function anchorFor(align: ParaAttrs['align'], lastInParagraph: boolean): Anchor {
  switch (align) {
    case 'right': case 'awayFromBinding': return 'end';
    case 'center': return 'middle';
    case 'justifyLastRight': return lastInParagraph ? 'end' : 'start';
    case 'justifyLastCenter': return lastInParagraph ? 'middle' : 'start';
    default: return 'start';
  }
}

export function isJustified(align: ParaAttrs['align'], lastInParagraph: boolean): boolean {
  if (align === 'justifyAll') return true;
  return align.startsWith('justify') && !lastInParagraph;
}

/** The tab stop that applies at `offset` pt from the column origin. */
export function nextTabStop(tabs: TabStop[], offset: number): TabStop {
  const stop = [...tabs].sort((a, b) => a.position - b.position).find((t) => t.position > offset + 0.01);
  if (stop) return stop;
  return { position: (Math.floor(offset / DEFAULT_TAB) + 1) * DEFAULT_TAB, alignment: 'left', leader: '' };
}

const LEFTISH = new Set<ParaAttrs['align']>(['left', 'toBinding', 'justifyLastLeft', 'justifyLastCenter', 'justifyLastRight', 'justifyAll']);

/**
 * Where a bulleted paragraph's text starts on its first line, relative to the column edge, the way
 * the engine's lineStarts() lays it down: the bullet at the first line indent, then its text after;
 * a tab there goes to the first stop past the bullet (the left indent counts as one for a hanging
 * indent), or the next default interval.
 */
export function bulletTextStart(para: ParaAttrs, measure: Measure): number {
  const first = para.leftIndent + para.firstLineIndent;
  const b = para.bullets;
  if (!b) return first;
  const after = first + measure(b.char || '•', b.fontId ?? para.fontId, para.pointSize, 0);
  if (!b.textAfter.includes('\t')) return after;
  const stops = (para.tabs ?? []).map((t) => t.position);
  if (para.firstLineIndent < 0) stops.push(para.leftIndent);
  return stops.sort((x, y) => x - y).find((s) => s > after + 1e-6) ?? (Math.floor(after / DEFAULT_TAB + 1e-6) + 1) * DEFAULT_TAB;
}

/**
 * The column edge a bulleted first line hangs its bullet from. The frame's column rect is exact
 * where the line agrees with it; where it does not (a split, a stroked frame, a wrap shift) the
 * line sits in a column the frame cannot describe, and its own start says where that is.
 */
function bulletOrigin(line: Pick<Line, 'x' | 'start'>, para: ParaAttrs, measure?: Measure, columnX?: number): number | undefined {
  const derived = measure ? line.x - bulletTextStart(para, measure) : undefined;
  if (columnX !== undefined && (derived === undefined || Math.abs(derived - columnX) <= ORIGIN_TOLERANCE)) return columnX;
  return derived;
}

/**
 * The bullet of a bulleted paragraph as a span of its own, on the first line only. Left-aligned
 * paragraphs only, the same guard as tabOrigin: a centred or right-aligned line carries its
 * bullet with the text, so neither the column edge nor the line's start says where it hangs, and
 * a marker placed by either would be a guess drawn over the page.
 */
export function bulletMarker(line: Pick<Line, 'x' | 'start'>, para: ParaAttrs, measure?: Measure, columnX?: number): LineProps['marker'] {
  const b = para.bullets;
  if (!b || line.start !== 0 || !LEFTISH.has(para.align)) return undefined;
  const origin = bulletOrigin(line, para, measure, columnX);
  if (origin === undefined) return undefined;
  return { text: b.char || '•', fontFamily: b.fontId ?? para.fontId, fontSize: para.pointSize, fill: para.fill?.hex || '#000000', letterSpacing: 0, x: origin + para.leftIndent + para.firstLineIndent };
}

/**
 * Where tab positions are measured from: the left edge of the text column this line sits in.
 * A paragraph that spans or splits columns sits in a column the frame's own column rects cannot
 * describe, so for left-aligned lines the origin is recovered from the line's own start and indents.
 * A bulleted first line starts after its bullet, so it is recovered the way the bullet's origin is.
 */
export function tabOrigin(line: Pick<Line, 'x' | 'start'>, para: ParaAttrs, columnX?: number, measure?: Measure): number {
  if (!LEFTISH.has(para.align)) return columnX ?? line.x;
  if (para.bullets && line.start === 0) {
    const origin = bulletOrigin(line, para, measure, columnX);
    if (origin !== undefined) return origin;
  }
  const indent = para.leftIndent + (line.start === 0 ? para.firstLineIndent : 0);
  return line.x - indent;
}

/** Leader characters that fill a tab gap, right-trimmed so the last partial repeat is dropped. */
export function leaderRun(leader: string, from: number, to: number, unit: number): string {
  if (!leader || unit <= 0) return '';
  const n = Math.floor((to - from) / unit);
  return n > 0 ? leader.repeat(n) : '';
}

function spanFor(text: string, a: TextAttrs): SpanProps {
  const span: SpanProps = {
    text: applyCapitalization(text, a.capitalization),
    fontFamily: a.fontId,
    fontSize: a.pointSize,
    fill: a.fill?.hex || '#000000',
    letterSpacing: trackingToSpacing(a.tracking ?? 0, a.pointSize),
  };
  if (a.baselineShift) span.dy = -a.baselineShift;
  if (a.capitalization === 'smallCaps') span.fontVariant = 'small-caps';
  if (a.underline) span.underline = true;
  if (a.strikethrough) span.strikethrough = true;
  return span;
}

interface Char { ch: string; attrs: TextAttrs }
interface Piece { text: string; attrs: TextAttrs; tab: boolean }

/** The line's characters with their resolved attributes, controls substituted, trailing spaces dropped. */
function charsOf(line: Line, paragraph: Paragraph, paraAttrs: ParaAttrs, lookup: StyleLookup, pageNumber: string): Char[] {
  const runs = paragraph.runs.length ? paragraph.runs : [{ start: 0, end: paragraph.text.length }];
  const out: Char[] = [];
  for (const run of runs) {
    const s = Math.max(run.start, line.start), e = Math.min(run.end, line.end);
    if (e <= s) continue;
    const attrs = resolveRunAttrs(paraAttrs, run, lookup);
    for (let i = s; i < e; i++) {
      const ch = paragraph.text[i];
      if (ch === AUTO_PAGE_NUMBER) { for (const c of pageNumber) out.push({ ch: c, attrs }); continue; }
      if (CONTROL.test(ch)) continue;
      out.push({ ch, attrs });
    }
  }
  if (out.length === 0) {
    for (const ch of substituteControls(paragraph.text.slice(line.start, line.end), pageNumber)) out.push({ ch, attrs: paraAttrs });
  }
  while (out.length && TRAILING.test(out[out.length - 1].ch)) out.pop();
  if (line.hyphenated && out.length && out[out.length - 1].ch !== '-') out.push({ ch: '-', attrs: out[out.length - 1].attrs });
  return out;
}

const sameAttrs = (a: TextAttrs, b: TextAttrs): boolean =>
  a === b || (a.fontId === b.fontId && a.pointSize === b.pointSize && a.fill?.hex === b.fill?.hex && a.tracking === b.tracking
    && a.baselineShift === b.baselineShift && a.capitalization === b.capitalization && a.underline === b.underline && a.strikethrough === b.strikethrough);

/** Consecutive characters with equal attributes, split at tabs. */
function pieces(chars: Char[]): Piece[] {
  const out: Piece[] = [];
  for (const c of chars) {
    if (c.ch === '\t') { out.push({ text: '\t', attrs: c.attrs, tab: true }); continue; }
    const last = out[out.length - 1];
    if (last && !last.tab && sameAttrs(last.attrs, c.attrs)) last.text += c.ch;
    else out.push({ text: c.ch, attrs: c.attrs, tab: false });
  }
  return out;
}

/**
 * Props for one InDesign line as a single SVG <text> at (x, baseline). Left-aligned lines keep the
 * exporter's glyph origin and get textLength only when the browser's natural width drifts from
 * InDesign's; right and centred lines anchor to the column edge the exporter reported; justified
 * lines and tabbed lines position each word or segment explicitly so the browser composes nothing.
 */
export function lineProps(line: Line, paragraph: Paragraph, lookup: StyleLookup, measure?: Measure, opts: LineOptions = {}): LineProps {
  const paraAttrs = resolveParaAttrs(paragraph, lookup);
  const lastInParagraph = opts.lastInParagraph ?? line.end >= paragraph.text.length;
  const chars = charsOf(line, paragraph, paraAttrs, lookup, opts.pageNumber ?? '');
  const parts = pieces(chars);
  const hasTab = parts.some((p) => p.tab);
  const justified = isJustified(paraAttrs.align, lastInParagraph);
  const anchor = anchorFor(paraAttrs.align, lastInParagraph);
  const spans: SpanProps[] = [];
  const props: LineProps = {
    key: `${line.paraId}:${line.start}`,
    paraId: line.paraId,
    frameId: line.frameId,
    start: line.start,
    end: line.end,
    x: anchor === 'end' ? line.x + line.width : anchor === 'middle' ? line.x + line.width / 2 : line.x,
    y: line.baseline,
    width: line.width,
    ascent: line.ascent,
    descent: line.descent,
    anchor,
    spans,
  };
  const marker = bulletMarker(line, paraAttrs, measure, opts.columnX);
  if (marker) props.marker = marker;
  const w = (text: string, a: TextAttrs): number => (measure ? measure(applyCapitalization(text, a.capitalization), a.fontId, a.pointSize, trackingToSpacing(a.tracking ?? 0, a.pointSize)) : 0);

  if (measure && hasTab) {
    // tabbed line: the segment after a tab starts at the stop, measured from the column origin
    const origin = tabOrigin(line, paraAttrs, opts.columnX, measure);
    let cursor = line.x;
    let pendingX: number | undefined;
    for (let i = 0; i < parts.length; i++) {
      const p = parts[i];
      if (p.tab) {
        const stop = nextTabStop(paraAttrs.tabs ?? [], cursor - origin);
        let x = origin + stop.position;
        let segW = 0;
        for (let j = i + 1; j < parts.length && !parts[j].tab; j++) segW += w(parts[j].text, parts[j].attrs);
        if (stop.alignment === 'right') x -= segW;
        else if (stop.alignment === 'center') x -= segW / 2;
        x = Math.max(cursor, x);
        if (stop.leader) {
          const unit = w(stop.leader, p.attrs);
          const text = leaderRun(stop.leader, cursor, x, unit);
          if (text) { const lead = spanFor(text, p.attrs); lead.x = cursor; spans.push(lead); }
        }
        cursor = x;
        pendingX = cursor;
        continue;
      }
      const span = spanFor(p.text, p.attrs);
      if (pendingX !== undefined) { span.x = pendingX; pendingX = undefined; }
      spans.push(span);
      cursor += w(p.text, p.attrs);
    }
    props.anchor = 'start';
    props.x = line.x;
    recordPositioned();
    return props;
  }

  if (measure && justified) {
    // justified line: the slack goes into the flush spaces if there are any, otherwise into every
    // word space; either way each word is placed explicitly so the browser composes nothing
    const words: Array<{ text: string; attrs: TextAttrs; space: boolean; flush: boolean }> = [];
    for (const p of parts) {
      let buf = '';
      for (const ch of p.text) {
        if (ch === ' ' || ch === FLUSH_SPACE) {
          if (buf) words.push({ text: buf, attrs: p.attrs, space: false, flush: false });
          buf = '';
          words.push({ text: ch, attrs: p.attrs, space: true, flush: ch === FLUSH_SPACE });
        } else buf += ch;
      }
      if (buf) words.push({ text: buf, attrs: p.attrs, space: false, flush: false });
    }
    const flushes = words.filter((wd) => wd.flush).length;
    // a flush space has no natural width of its own: it is only the slack it absorbs
    const natural = words.reduce((sum, wd) => sum + (wd.flush ? 0 : w(wd.text, wd.attrs)), 0);
    const elastic = flushes > 0 ? flushes : words.filter((wd) => wd.space).length;
    const extra = elastic > 0 ? (line.width - natural) / elastic : 0;
    const stretches = (wd: { space: boolean; flush: boolean }) => (flushes > 0 ? wd.flush : wd.space);
    let cursor = line.x;
    for (const wd of words) {
      const width = wd.flush ? 0 : w(wd.text, wd.attrs);
      if (!wd.space) {
        const span = spanFor(wd.text, wd.attrs);
        span.x = cursor;
        spans.push(span);
      }
      cursor += width + (stretches(wd) ? extra : 0);
    }
    props.anchor = 'start';
    props.x = line.x;
    recordPositioned();
    return props;
  }

  for (const p of parts) spans.push(spanFor(p.tab ? ' ' : p.text, p.attrs));

  if (measure && spans.length && line.width > 0 && anchor === 'start' && !hasTab && !justified) {
    // `Line.width` is ink width: measured to the last non-whitespace character, with no trailing
    // space and no paragraph return advance. InDesign adds no tracking after the last glyph of a
    // line either, so drop the final letter-spacing before comparing.
    const last = spans[spans.length - 1];
    const trimmed = spans.reduce((sum, sp) => sum + measure(sp.text, sp.fontFamily, sp.fontSize, sp.letterSpacing), 0) - last.letterSpacing;
    const delta = trimmed - line.width;
    const adjust = Math.abs(delta) > WIDTH_TOLERANCE;
    props.naturalWidth = trimmed;
    if (adjust) props.textLength = line.width;
    recordLine(delta, adjust);
  }
  return props;
}

/** Lines of a story grouped by paragraph, for highlighting the paragraph under the caret. */
export function linesByParagraph<T extends { paraId: string }>(lines: T[]): Map<string, T[]> {
  const m = new Map<string, T[]>();
  for (const l of lines) {
    const arr = m.get(l.paraId) ?? [];
    arr.push(l);
    m.set(l.paraId, arr);
  }
  return m;
}
